/**
 * Exact arithmetic on the decimal strings the API sends — for the rare display
 * that has to combine amounts (a chart's "Other" bar).
 *
 * Money is never added through `Number`: 0.1 + 0.2 is 0.30000000000000004, and
 * a total that disagrees with its parts in the last digit is exactly the kind
 * of discrepancy a finance reader stops trusting a screen over. The strings are
 * scaled to a common number of places and summed as `BigInt`.
 *
 * Callers pass amounts of ONE currency. Adding across currencies is not an
 * arithmetic problem, it is an unapproved exchange rate, and Layer 1 has none.
 */
const DECIMAL = /^(-)?(\d+)(?:\.(\d+))?$/;

/** A decimal as an integer and its scale: "12.50" → { units: 1250n, scale: 2 }. */
function parse(value: string): { units: bigint; scale: number } {
  const match = DECIMAL.exec(value.trim());

  if (!match) throw new Error(`Not a decimal: ${value}`);

  const frac = match[3] ?? '';
  const units = BigInt(match[2] + frac);

  return { units: match[1] === '-' ? -units : units, scale: frac.length };
}

function render(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const int = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale);

  return `${negative ? '-' : ''}${int}${scale > 0 ? `.${frac}` : ''}`;
}

/** The exact product of two decimals — a unit price times a quantity. */
export function mulDecimals(a: string, b: string): string {
  const x = parse(a);
  const y = parse(b);

  return render(x.units * y.units, x.scale + y.scale);
}

/** `rate` percent of `value`, exactly: 15 percent of 200.50 is 30.0750. */
export function percentOf(value: string, rate: string): string {
  const product = parse(mulDecimals(value, rate));

  return render(product.units, product.scale + 2);
}

/** a − b, exactly. */
export function subDecimals(a: string, b: string): string {
  const x = parse(a);
  const y = parse(b);
  const scale = Math.max(x.scale, y.scale);

  return render(
    x.units * 10n ** BigInt(scale - x.scale) - y.units * 10n ** BigInt(scale - y.scale),
    scale,
  );
}

/** −1, 0 or 1, exactly — never through a float. */
export function compareDecimals(a: string, b: string): number {
  const difference = parse(subDecimals(a, b)).units;

  return difference === 0n ? 0 : difference < 0n ? -1 : 1;
}

/**
 * The decimal without insignificant trailing zeros and without grouping —
 * "60.0000" → "60" — the form an <input type="number"> value or `max` takes.
 * Every significant digit is kept.
 */
export function trimDecimal(value: string): string {
  const match = DECIMAL.exec(value.trim());

  if (!match) return value;

  const frac = (match[3] ?? '').replace(/0+$/, '');

  return `${match[1] ?? ''}${match[2]}${frac ? `.${frac}` : ''}`;
}

/** Whether a string is an exact decimal at all. */
export function isDecimal(value: string): boolean {
  return DECIMAL.test(value.trim());
}

/**
 * `value` at `scale` places, half away from zero — the backend's Money rounding
 * (HalfUp at scale 4), so a preview computed here agrees with the figure the
 * server will store: 1.00005 → "1.0001", −1.00005 → "−1.0001".
 */
export function roundDecimal(value: string, scale: number): string {
  const x = parse(value);

  if (x.scale <= scale) return render(x.units * 10n ** BigInt(scale - x.scale), scale);

  const divisor = 10n ** BigInt(x.scale - scale);
  const negative = x.units < 0n;
  const magnitude = negative ? -x.units : x.units;
  const quotient = magnitude / divisor;
  const rounded = (magnitude % divisor) * 2n >= divisor ? quotient + 1n : quotient;

  return render(negative ? -rounded : rounded, scale);
}

export function sumDecimals(values: readonly string[]): string {
  const parsed = values.map((value) => {
    const match = DECIMAL.exec(value.trim());

    if (!match) throw new Error(`Not a decimal: ${value}`);

    return { negative: match[1] === '-', int: match[2], frac: match[3] ?? '' };
  });

  const places = Math.max(0, ...parsed.map((p) => p.frac.length));
  const total = parsed.reduce((sum, p) => {
    const scaled = BigInt(p.int + p.frac.padEnd(places, '0'));

    return sum + (p.negative ? -scaled : scaled);
  }, 0n);

  const negative = total < 0n;
  const digits = (negative ? -total : total).toString().padStart(places + 1, '0');
  const int = digits.slice(0, digits.length - places);
  const frac = digits.slice(digits.length - places);

  return `${negative ? '-' : ''}${int}${places > 0 ? `.${frac}` : ''}`;
}
